// eslint-disable-next-line max-classes-per-file
import type { Review } from "@/components/global/ContentComponents/ContentListComponent/ContentListComponentInterface";
import { convertToBoolean } from "@/utils/common/formatHelper/booleanUtills";
import { calWeightingScore } from "@/utils/common/mapHelper/mapHelperUtils";
import { PriceDisplayEnum } from "@/utils/enums/PriceDisplayEnum";
import { mapPerks } from "@/utils/hooks/hotels/usePrivilege";

/* eslint-disable max-classes-per-file */
export interface RequestHotelAvailableInterface {
  city?: number;
  area?: number;
  hotel?: number;
  checkIn: string;
  checkOut: string;
  adults: number;
  children: number;
  childAge?: string;
  rooms: number;
  destinationDisplay?: string;
  session?: string;
  isGreenCertificate?: boolean;
  couponCode?: string;
  bookingNo?: string;
  travelPeriodStart?: string;
  travelPeriodEnd?: string;
}
export type PartialCheckinCheckout = Pick<
  RequestRawHotelAvailableForMappingApiInterface,
  "hotelId"
> &
  Partial<
    Pick<RequestRawHotelAvailableForMappingApiInterface, "checkin" | "checkout">
  >;
export interface RequestRawHotelAvailableForMappingApiInterface {
  destinationId: number;
  checkin: string;
  checkout: string;
  totalRoom: number;
  totalAdult: number;
  totalChild: number;
  countryOfPassport: string;
  page: number;
  limit: number;
  isLogin: boolean;
  preferredCurrency: string;
  hotelId: number;
  latitude?: string;
  longitude?: string;
  childAge?: number[] | string;
}
export class PerkDetailPresenter {
  detail: string;

  name: string;

  constructor(res: any) {
    this.detail = res?.detail || "";
    this.name = res?.name || "";
  }
}
export class PerkDataPresenter {
  freebies: FreebiesInterface[];

  privileges: PrivilegesInterface[];

  constructor(res: any) {
    this.freebies =
      res?.freebies?.map(
        (item: PerkDetailPresenter) => new PerkDetailPresenter(item),
      ) || [];

    this.privileges =
      res?.privileges?.map(
        (item: PerkDetailPresenter) => new PerkDetailPresenter(item),
      ) || [];
  }
}
export interface PerkDataInterface {
  freebies: FreebiesInterface[];
  privileges: PrivilegesInterface[];
}
export class ResponseHotelPresenter {
  rackRateMarkup: number;

  id: number;

  name: string;

  hotelName: { th: string; en: string } = { th: "", en: "" };

  description: string;

  starRating: number;

  latitude: number;

  longitude: number;

  phoneNumber: string;

  faxNumber: string;

  address: string;

  countryName: string;

  cityName: string;

  areaName: string;

  destinationId: number;

  photoGallery: string[];

  useful: any[]; // Adjust the type based on your actual data structure

  facilities: string[];

  amenities: any[]; // Adjust the type based on your actual data structure

  specialDeal: boolean;

  perks?: {
    en: string[];
    th: string[];
  };

  perkData: PerkDataInterface;

  observation: string;

  currency: string;

  price: number;

  isRegister: boolean;

  isThaiHotelMember: boolean;

  shaPlus: boolean;

  shaExtraPlus: boolean;

  isPayAtHotel: number;

  isPayNow: number;

  isFreeCancellation: number;

  review: {
    rating: string;
    text_rating_detail: string;
    num_reviews: string;
  };

  isRecommend: number;

  usefulDetail: any[]; // Adjust the type based on your actual data structure

  facilitiesDetail: {
    groupId: number;
    groupName: string;
    details: Array<{
      id: number;
      name: string;
    }>;
  }[];

  amenitiesDetail: any[]; // Adjust the type based on your actual data structure

  isTop100: number;

  weightingScore: number;

  isFavourite: boolean;

  slug: string;

  countrySlug: string;

  citySlug: string;

  areaSlug: string;

  hotelSlugPath: string;

  rackRate: number;

  roomLeft: number;

  vat: number;

  priceWithoutVat: number;

  rackRateWithoutVat: number;

  priceWithPriceType: number;

  isGreenCertificate: boolean;

  propertyType: string;

  greenCertifications?: {
    id: number;
    nameEn: string;
    nameTh: string;
  }[];

  isPayLater: number;

  constructor(
    data: any,
    day?: number,
    vatType?: PriceDisplayEnum,
    privileges?: DataHotelPrivileges,
  ) {
    this.rackRateMarkup = data.rackRateMarkup || 0;
    this.id = data.id || 0;
    this.name = data.name || "";
    this.description = data.description || "";
    this.starRating = data.starRating || 0;
    this.latitude = data.latitude || 0;
    this.longitude = data.longitude || 0;
    this.phoneNumber = data.phoneNumber || "";
    this.faxNumber = data.faxNumber || "";
    this.address = data.address || "";
    this.countryName = data.countryName || "";
    this.cityName = data.cityName || "";
    this.areaName = data.areaName || "";
    this.destinationId = data.destinationId || 0;
    this.photoGallery = data.photoGallery || data.images || [];
    this.useful = data.useful || [];
    this.facilities = data.facilities || [];
    this.amenities = data.amenities || [];
    this.specialDeal = this.setSpecialDeal(data, privileges);

    this.perks = {
      en: (data?.perks && data?.perks["en-us"]) || [],
      th: (data?.perks && data?.perks["th-th"]) || [],
    };
    this.perkData = this.setPerk(data, privileges);

    this.observation = data.observation || "";
    this.currency = data.currency || "";
    this.price = data.price || 0;
    this.isRegister = convertToBoolean(data.isRegister || false);
    this.isThaiHotelMember = convertToBoolean(data.isThaiHotelMember || false);
    this.shaPlus = convertToBoolean(data.shaPlus || false);
    this.shaExtraPlus = convertToBoolean(data.shaExtraPlus || false);
    this.review = {
      rating: data.review?.rating || "",
      text_rating_detail: data.review?.text_rating_detail || "",
      num_reviews: data.review?.num_reviews || "",
    };
    this.isRecommend = data.isRecommend || 0;
    this.usefulDetail = data.usefulDetail || [];
    this.facilitiesDetail = data.facilitiesDetail || [];
    this.amenitiesDetail = data.amenitiesDetail || [];
    this.isTop100 = data.is_top100 || 0;
    this.isPayAtHotel = data.isPayAtHotel || 0;
    this.isPayNow = data.isPayNow || 0;
    this.isFreeCancellation = data.isFreeCancellation || 0;
    this.weightingScore = calWeightingScore(
      this.isTop100,
      this.review.rating ? +this.review.rating : 0,
      this.starRating,
    );
    this.isFavourite = convertToBoolean(data.isFavourite || false);
    this.slug = data.slug || "";
    this.countrySlug = data.countrySlug || "";
    this.citySlug = data.citySlug || "";
    this.areaSlug = data.areaSlug || "";
    this.rackRate = data.rackRate || 0;
    this.roomLeft = data.roomLeft || 0;
    this.hotelSlugPath = `${this.countrySlug}/${this.citySlug}/${this.areaSlug}/${this.slug}`;
    this.vat = data.vat || 0;
    this.priceWithoutVat = data.priceWithoutVat || 0;
    this.rackRateWithoutVat = data.rackRateWithoutVat || 0;
    const calcPrice = () => {
      const respPrice = data.price || 0;
      const respPriceWithOutVat = data.priceWithoutVat || 0;
      if (!day || !vatType) return respPrice;
      if (vatType === PriceDisplayEnum.NOT_INCLUDING_TAX)
        return respPriceWithOutVat / day;
      if (vatType === PriceDisplayEnum.INCLUDING_TAX) return respPrice / day;
      return respPrice;
    };
    this.priceWithPriceType = calcPrice();
    this.isGreenCertificate = data.isGreenCertificate || false;
    this.greenCertifications = data.greenCertifications || [];
    this.isPayLater = data?.isPayLater || 0;
    this.propertyType = data?.property_type || "";
    this.hotelName = Object.assign(this.hotelName, data?.hotelName);
  }

  private setPerk(
    data: any,
    privileges?: DataHotelPrivileges,
  ): PerkDataInterface {
    return mapPerks({
      hotelId: data?.id,
      privileges,
    });
  }

  private setSpecialDeal(data: any, privileges?: DataHotelPrivileges): boolean {
    if (convertToBoolean(data?.specialDeal || false)) {
      return true;
    }
    if (!privileges) {
      return false;
    }

    // Merge ids and includeIds
    const mergedSpecialDealIds = [
      ...(privileges.specialDeals?.ids || []),
      ...(privileges.specialDeals?.includeIds || []),
    ];

    if (mergedSpecialDealIds.length === 0) {
      return false;
    }

    // Check if this.id exists in the merged array
    return mergedSpecialDealIds.some(
      (specialDealId) => specialDealId === this.id,
    );
  }
}

export class ResponseHotelRecommendSide {
  id: number = 0;

  name: string = "";

  review: Review;

  starRating: number;

  cityName: string = "";

  areaName: string = "";

  price: number = 0;

  currency: string = "";

  sessionSearch: string = "";

  lat: number = 0;

  lng: number = 0;

  slugPath: string = "";

  countrySlug: string = "";

  citySlug: string = "";

  areaSlug: string = "";

  slug: string = "";

  photoGallery: string = "";

  constructor(data: any) {
    this.id = data.id || 0;
    this.name = data.name || "";
    this.photoGallery = data.photoGallery || "";
    this.review = {
      rating: data.review?.rating || "",
      text_rating_detail: data.review?.text_rating_detail || "",
      num_reviews: data.review?.num_reviews || "",
    };
    this.starRating = data.starRating || "";
    this.cityName = data.cityName || "";
    this.areaName = data.areaName || "";
    this.price = data.price || 0;
    this.currency = data.currency || "";
    this.sessionSearch = data.sessionSearch || "";
    this.lat = data.lat || 0;
    this.lng = data.lng || 0;
    this.countrySlug = data.countrySlug || "";
    this.citySlug = data.citySlug || "";
    this.areaSlug = data.areaSlug || "";
    this.slug = data.slug || "";
    this.slugPath = `${this.countrySlug}/${this.citySlug}/${this.areaSlug}/${this.slug}`;
  }
}
export class ResponseHotelAvailablePresenter {
  sessionSearch: string = "";

  hotels: ResponseHotelPresenter[] = [];

  total: number = 0;

  cache: boolean = false;

  recommend: ResponseHotelRecommendSide[] = [];

  botURL: string = "";

  botURLNew: string = "";

  destinationTitle: string = "";

  constructor(
    data: any,
    day?: number,
    vatType?: PriceDisplayEnum,
    privileges?: DataHotelPrivileges,
  ) {
    this.sessionSearch = data.sessionSearch || "";
    this.hotels = (data.hotels || []).map(
      (hotel: any) =>
        new ResponseHotelPresenter(hotel, day, vatType, privileges),
    );
    this.total = data.total || 0;
    this.cache = data.cache || false;
    this.recommend = data.recommend
      ? (data.recommend as any[]).map((value) => {
          return new ResponseHotelRecommendSide(value);
        })
      : [];
    this.botURL = data.botURL || "";
    this.botURLNew = data.botURLNew || "";
    if (data.areaName) {
      this.destinationTitle = `${data.areaName}, ${data.cityName}, ${data.countryName}`;
    } else if (data.cityName) {
      this.destinationTitle = `${data.cityName}, ${data.countryName}`;
    } else if (data.countryName) {
      this.destinationTitle = `${data.countryName}`;
    }
  }
}

interface FreebiesInterface {
  detail: string;
  name: string;
}

export interface PrivilegesInterface {
  detail: string;
  name: string;
}

export interface HotelPerkInterface {
  ids: number[];
  name: string;
  freebies: FreebiesInterface[];
  privileges: PrivilegesInterface[];
}

export class DataHotelPrivileges {
  specialDeals: {
    ids: number[];
    includeIds: number[];
    wholesaleIds: number[];
  };

  reccommends: {
    ids: number[];
    includeIds: number[];
    wholesaleIds: number[];
  };

  perks: HotelPerkInterface[];

  constructor(data: any) {
    this.specialDeals = {
      ids: data.specialDeals?.ids || [],
      includeIds: data.specialDeals?.includeIds || [],
      wholesaleIds: data.specialDeals?.wholesaleIds || [],
    };
    this.reccommends = {
      ids: data.reccommends?.ids || [],
      includeIds: data.reccommends?.includeIds || [],
      wholesaleIds: data.reccommends?.wholesaleIds || [],
    };
    this.perks = (data?.perks || []).map((perk: HotelPerkInterface) => ({
      ids: perk?.ids || [],
      name: perk?.name || "",
      freebies: (perk.freebies || []).map((freebie) => ({
        name: freebie.name,
        detail: freebie.detail,
      })),
      privileges: (perk.privileges || []).map((privilege) => ({
        name: privilege.name,
        detail: privilege.detail,
      })),
    }));
  }
}

export class ResponseHotelPrivileges {
  status: boolean;

  message: string;

  code: string;

  data: DataHotelPrivileges;

  constructor(data: any) {
    this.status = data.status || false;
    this.message = data.message || "";
    this.code = data.code || "";
    this.data = data.data
      ? new DataHotelPrivileges(data.data)
      : new DataHotelPrivileges({});
  }
}
